import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";

import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

import JobService from "../../service/job";
import UserService from "../../service/user";
import JobInstructions from "./JobInstructions";
import ArtworkApproval from "./ArtworkApproval";
import ProductionDetails from "./ProductionDetails";
import CommonLayout from "../Common/Layout";
import { showToast } from "../../store/actions";

const useStyles = makeStyles((theme) =>
  createStyles({
    paper: {
      padding: "20px",
    },
    detailsHeader: {
      fontWeight: "normal",
      "&::after": {
        content: "''",
        display: "block",
        width: "40px",
        height: "3px",
        background: "#EC5143",
        margin: "5px 0 0",
        borderRadius: "5px",
      },
    },
  })
);

const AllCombined = () => {
  const { job_id } = useParams();

  const location = useLocation();
  const isSupplierDashboard = location.state?.isSupplierDashboard;
  const navigate = useNavigate();
  const isNewJob = !job_id;
  const user = useSelector((state) => state.user);
  const role = user.currentUser?.Role_id;
  const dispatch = useDispatch();

  const classes = useStyles();
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [jobName, setJobName] = useState(""); // prettier-ignore
  const [customerId, setCustomerId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [orderReceivedDate, setOrderReceivedDate] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [purchaseOrderNo, setPurchaseOrderNo] = useState("");
  const [quoteNo, setQuoteNo] = useState("");
  const [isSampleRequired, setIsSampleRequired] = useState(true);

  // Part 2 State
  const [logoReceivedDate, setLogoReceivedDate] = useState(null);
  const [artworkApprovedStatus, setArtworkApprovedStatus] = useState("");
  const [sampleImageEmailedDate, setSampleImageEmailedDate] = useState(null);
  const [logoTemplateUploadedDate, setLogoTemplateUploadedDate] = useState(null);
  const [artworkApprovedDate, setArtworkApprovedDate] = useState(null);
  const [sampleImageApproved, setSampleImageApproved] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState(""); // prettier-ignore
  const [artworks, setArtworks] = useState([]);
  const [samples, setSamples] = useState([]);
  const [artworkFilesToUpload, setArtworkFilesToUpload] = useState([]);
  const [sampleFilesToUpload, setSampleFilesToUpload] = useState([]);

  // Part 3 State
  const [productionStartDate, setProductionStartDate] = useState(null);
  const [productionDays, setProductionDays] = useState("");
  const [estimatedCompletionDate, setEstimatedCompletionDate] = useState(null);
  const [actualCompletionDate, setActualCompletionDate] = useState(null);
  const [freightShippingMethod, setFreightShippingMethod] = useState("");
  const [confirmedDepartedDate, setConfirmedDepartedDate] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(null);
  const [trackingDetails, setTrackingDetails] = useState("");
  const [arrivalDate, setArrivalDate] = useState(null);
  const [confirmedDeliveryDate, setConfirmedDeliveryDate] = useState(null);
  const [orderStatus, setOrderStatus] = useState("progressing");
  const [delayReason, setDelayReason] = useState("");

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      const userService = new UserService();
      const jobService = new JobService();

      const customersResponse = await userService.getCustomers();
      if (customersResponse.success && customersResponse.data?.userList) {
        setCustomers(customersResponse.data.userList);
      }

      // Fetch suppliers if Admin (1) or Employee (2)
      if (role === 1 || role === 2) {
        const suppliersResponse = await userService.getSuppliers();
        if (suppliersResponse.success && suppliersResponse.data?.userList) {
          setSuppliers(suppliersResponse.data.userList);
        }
      }

      if (isNewJob) {
        setLoading(false);
        return;
      }

      // The rest of the logic is for existing jobs
      fetchJobData();
    };

    const fetchJobData = async () => {
      const jobService = new JobService();
      const jobResponse = await jobService.getJob(job_id);

      if (jobResponse.success && jobResponse.data) {
        const job = jobResponse.data;
        setJobName(job.Name);
        setCustomerId(job.Customer_id);
        setSupplierId(job.Supplier_id);
        setDescription(job.Description);
        setPrice(job.Amount);
        setPurchaseOrderNo(job.ProductLink);
        setQuantity(job.Quantity);
        setOrderReceivedDate(job.OrderReceivedDate);
        setFreightShippingMethod(job.FreightShippingMethod);
        setArtworkApprovedDate(job.ArtworkApprovedDate);
        setSampleImageApproved(job.SampleImageApprovedDate || job.SampleImageApproved); // Legacy fallback
        setProductionDays(job.ProductionTime);
        setTrackingDetails(job.TrackingNumber);
        setArrivalDate(job.ArrivalDate || job.ConfirmedArrivalDate); // Legacy fallback
        setDeliveryAddress(job.LocalDeliveryAddress);
        setConfirmedDeliveryDate(job.JobCompletedDeliveryDate || job.DateOrderDelivered); // Legacy fallback
        setArtworks(job.Artworks || []);
        setSamples(job.Samples || []);
        setQuoteNo(job.QuoteNo);
        setOrderStatus(job.Status);
        setApprovalNotes(job.ArtworkComments);
        setDeliveryDate(job.DeliveryDate);
        setLogoReceivedDate(job.LogoReceivedDate);
        setIsSampleRequired(job.SampleRequired === 1);
        setArtworkApprovedStatus(job.ArtworkStatus);
        setSampleImageEmailedDate(job.SampleImageEmailedDate);
        setLogoTemplateUploadedDate(job.LogoTemplateUploadedDate);
        setProductionStartDate(job.ProductionStartDate);
        setEstimatedCompletionDate(job.EstimatedCompletionDate || job.ProductionCompleteDate); // Legacy fallback
        setActualCompletionDate(job.ActualCompletionDate);
        setConfirmedDepartedDate(job.ConfirmedDepartedDate);
        setExpectedDeliveryDate(job.ExpectedDeliveryDate);
        setDelayReason(job.DelayReason);
      }
      setLoading(false);
    };

    fetchData();
  }, [job_id, isNewJob]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToDashboard = () => {
    if (isSupplierDashboard) {
      navigate("/admin/supplier-dashboard");
    } else {
      navigate("/dashboard");
    }
  };

  const handleSave = () => {
    setLoading(true);
    const jobService = new JobService();
    const formData = new FormData();

    // Append main fields
    formData.append("Name", jobName);
    if (customerId) formData.append("Customer_id", customerId);
    if (supplierId) formData.append("Supplier_id", supplierId);
    formData.append("Description", description);
    formData.append("Amount", price || 0);
    formData.append("ProductLink", purchaseOrderNo);
    formData.append("QuoteNo", quoteNo);
    formData.append("Status", orderStatus);
    formData.append("ArtworkComments", approvalNotes);
    formData.append("ArtworkStatus", artworkApprovedStatus);
    formData.append("DelayReason", delayReason);
    formData.append("SampleRequired", isSampleRequired ? 1 : 0);
    formData.append("Employee_id", user.currentUser.User_id);

    // Append dates
    if (orderReceivedDate) formData.append("OrderReceivedDate", orderReceivedDate);
    if (deliveryDate) formData.append("DeliveryDate", deliveryDate);
    if (logoReceivedDate) formData.append("LogoReceivedDate", logoReceivedDate);
    if (sampleImageEmailedDate) formData.append("SampleImageEmailedDate", sampleImageEmailedDate);
    if (logoTemplateUploadedDate) formData.append("LogoTemplateUploadedDate", logoTemplateUploadedDate);
    if (productionStartDate) formData.append("ProductionStartDate", productionStartDate);
    if (actualCompletionDate) formData.append("ActualCompletionDate", actualCompletionDate);
    if (confirmedDepartedDate) formData.append("ConfirmedDepartedDate", confirmedDepartedDate);
    if (expectedDeliveryDate) formData.append("ExpectedDeliveryDate", expectedDeliveryDate);
    if (sampleImageApproved) formData.append("SampleImageApproved", sampleImageApproved);
    if (arrivalDate) formData.append("ArrivalDate", arrivalDate);

    // Append new direct fields
    if (quantity) formData.append("Quantity", quantity);
    if (freightShippingMethod) formData.append("FreightShippingMethod", freightShippingMethod);
    if (artworkApprovedDate) formData.append("ArtworkApprovedDate", artworkApprovedDate);
    if (sampleImageApproved) formData.append("SampleApprovedDate", sampleImageApproved);
    if (productionDays) formData.append("ProductionTime", productionDays);
    if (estimatedCompletionDate) formData.append("ProductionCompleteDate", estimatedCompletionDate);
    if (trackingDetails) formData.append("TrackingNumber", trackingDetails);
    if (arrivalDate) formData.append("ConfirmedArrivalDate", arrivalDate);
    if (deliveryAddress) formData.append("LocalDeliveryAddress", deliveryAddress);
    if (confirmedDeliveryDate) formData.append("JobCompletedDeliveryDate", confirmedDeliveryDate);
    // This is for legacy compatibility if needed, can be removed if DateOrderDelivered is fully deprecated
    if (confirmedDeliveryDate) formData.append("DateOrderDelivered", confirmedDeliveryDate);

    // Append new files for upload
    artworkFilesToUpload.forEach((file) => formData.append("ArtworkFile", file, `2,${file.name}`));
    sampleFilesToUpload.forEach((file) => formData.append("SampleFile", file, `3,${file.name}`));

    const action = isNewJob ? jobService.createJob(formData) : jobService.updateJob(job_id, formData);
    const successMessage = isNewJob ? "Job created successfully." : "Job updated successfully.";
    const errorMessage = isNewJob ? "Error creating job." : "Error updating job.";

    action
      .then((response) => {
        if (response.success) {
          dispatch(showToast({ message: successMessage, type: "success" }));
          if (isNewJob && response.data?.Job_id) {
            // On create, navigate to the new job's page
            navigate(`/admin/jobs/${response.data.Job_id}`, {
              state: { isSupplierDashboard: !!isSupplierDashboard },
            });
          } else if (!isNewJob) {
            // On update, navigate to the job listing screen
            navigateToDashboard()
          }
        } else {
          dispatch(showToast({ message: response.message || errorMessage, type: "error" }));
        }
      })
      .catch((err) => {
        dispatch(showToast({ message: `An unexpected error occurred. ${err.message}`, type: "error" }));
        console.error("Update Job Error:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const breadcrumbArr = [
    {
      link: isSupplierDashboard ? "/admin/supplier-dashboard" : "/dashboard",
      displayName: "Jobs",
      active: false,
    },
    { link: "#", displayName: isNewJob ? "Add Job" : "Job View", active: true },
  ];

  const isReadOnly = role === 3;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "70vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CommonLayout title={isNewJob ? "Add New Job" : `Job View - ${job_id}`} breadcrumbArr={breadcrumbArr}>
      {/* This new Box creates a scrollable container with a fixed height */}
      <Box sx={{ height: "75vh", overflow: "auto", p: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={3}>
            {/* Part 1: Job Name & Instructions */}
            <Grid item xs={12} md={4}>
              <JobInstructions
                classes={classes}
                jobName={jobName}
                isReadOnly={isReadOnly}
                setJobName={setJobName}
                customers={customers}
                customerId={customerId}
                setCustomerId={setCustomerId}
                suppliers={suppliers}
                supplierId={supplierId}
                setSupplierId={setSupplierId}
                canViewSuppliers={(role === 1 || role === 2) && !!isSupplierDashboard}
                canViewCustomers={role !== 4 && !isSupplierDashboard}
                isNewJob={isNewJob}
                description={description}
                setDescription={setDescription}
                quantity={quantity}
                setQuantity={setQuantity}
                price={price}
                setPrice={setPrice}
                orderReceivedDate={orderReceivedDate}
                setOrderReceivedDate={setOrderReceivedDate}
                deliveryDate={deliveryDate}
                setDeliveryDate={setDeliveryDate}
                purchaseOrderNo={purchaseOrderNo}
                setPurchaseOrderNo={setPurchaseOrderNo}
                quoteNo={quoteNo}
                setQuoteNo={setQuoteNo}
              />
            </Grid>

            {/* Part 2: Artwork Approval */}
            <Grid item xs={12} md={4}>
              <ArtworkApproval
                classes={classes}
                logoReceivedDate={logoReceivedDate}
                isReadOnly={isReadOnly}
                setLogoReceivedDate={setLogoReceivedDate}
                artworkApprovedStatus={artworkApprovedStatus}
                setArtworkApprovedStatus={setArtworkApprovedStatus}
                sampleImageEmailedDate={sampleImageEmailedDate}
                setSampleImageEmailedDate={setSampleImageEmailedDate}
                logoTemplateUploadedDate={logoTemplateUploadedDate}
                setLogoTemplateUploadedDate={setLogoTemplateUploadedDate}
                artworkApprovedDate={artworkApprovedDate}
                setArtworkApprovedDate={setArtworkApprovedDate}
                sampleImageApproved={sampleImageApproved}
                setSampleImageApproved={setSampleImageApproved}
                approvalNotes={approvalNotes}
                setApprovalNotes={setApprovalNotes}
                artworks={artworks}
                samples={samples}
                setArtworkFilesToUpload={setArtworkFilesToUpload}
                setSampleFilesToUpload={setSampleFilesToUpload}
                isSampleRequired={isSampleRequired}
                setIsSampleRequired={setIsSampleRequired}
              />
            </Grid>

            {/* Part 3: Production Details */}
            <Grid item xs={12} md={4}>
              <ProductionDetails
                classes={classes}
                productionStartDate={productionStartDate}
                isReadOnly={isReadOnly}
                setProductionStartDate={setProductionStartDate}
                productionDays={productionDays}
                setProductionDays={setProductionDays}
                estimatedCompletionDate={estimatedCompletionDate}
                setEstimatedCompletionDate={setEstimatedCompletionDate}
                actualCompletionDate={actualCompletionDate}
                setActualCompletionDate={setActualCompletionDate}
                freightShippingMethod={freightShippingMethod}
                setFreightShippingMethod={setFreightShippingMethod}
                confirmedDepartedDate={confirmedDepartedDate}
                setConfirmedDepartedDate={setConfirmedDepartedDate}
                deliveryAddress={deliveryAddress}
                setDeliveryAddress={setDeliveryAddress}
                expectedDeliveryDate={expectedDeliveryDate}
                setExpectedDeliveryDate={setExpectedDeliveryDate}
                trackingDetails={trackingDetails}
                setTrackingDetails={setTrackingDetails}
                arrivalDate={arrivalDate}
                setArrivalDate={setArrivalDate}
                confirmedDeliveryDate={confirmedDeliveryDate}
                setConfirmedDeliveryDate={setConfirmedDeliveryDate}
                orderStatus={orderStatus}
                setOrderStatus={setOrderStatus}
                delayReason={delayReason}
                setDelayReason={setDelayReason}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* Action Buttons are moved outside the scrollable area */}
      <Box sx={{ p: 3, pt: 1, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {!isReadOnly && (
          <Button variant="outlined" color="secondary" onClick={navigateToDashboard}>
            Cancel
          </Button>
        )}
        {!isReadOnly && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              loading ||
              !jobName ||
              (role !== 4 && (isSupplierDashboard ? !supplierId : !customerId))
            }
          >
            Save
          </Button>
        )}
      </Box>
    </CommonLayout>
  );
};

export default AllCombined;
